/**
 * Utilidad para redimensionar imágenes en el cliente ANTES de subirlas a Storage.
 *
 * - Reduce drásticamente el bandwidth de subida y bajada.
 * - Se ejecuta antes de la encriptación, por lo que es compatible con archivos cifrados.
 * - Solo procesa imágenes (image/*). PDFs y otros tipos pasan intactos.
 * - GIFs animados y SVGs NO se redimensionan (perderían animación / vectorialidad).
 *
 * Uso:
 *   const optimized = await resizeImageIfNeeded(file, { maxDimension: 1600 });
 *   await uploadBytes(ref, optimized);
 */

export interface ResizeOptions {
  /** Lado más largo máximo en píxeles. Default: 1600. */
  maxDimension?: number;
  /** Calidad JPEG/WebP de 0 a 1. Default: 0.85. */
  quality?: number;
  /**
   * Tipo MIME de salida. Default: image/jpeg para fotos, image/png si la
   * imagen original es PNG con transparencia.
   */
  mimeType?: "image/jpeg" | "image/webp" | "image/png";
}

/** Tipos que se redimensionan. Excluye SVG (vectorial) y GIF (puede ser animado). */
const RESIZABLE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

/**
 * Si el archivo es una imagen redimensionable y excede maxDimension, retorna
 * una versión más pequeña. Si no, retorna el archivo original sin tocar.
 *
 * Nunca lanza errores: si algo falla (browser sin canvas, formato no soportado,
 * decodificación fallida), retorna el archivo original.
 */
export async function resizeImageIfNeeded(
  file: File,
  opts: ResizeOptions = {},
): Promise<File> {
  const maxDimension = opts.maxDimension ?? 1600;
  const quality = opts.quality ?? 0.85;

  // No es imagen → pasar tal cual
  if (!file.type.startsWith("image/")) return file;

  // Tipo no resizable (svg, gif animado, etc.) → pasar tal cual
  if (!RESIZABLE_TYPES.has(file.type)) return file;

  // SSR / entorno sin DOM → no hacer nada
  if (typeof window === "undefined" || typeof document === "undefined") {
    return file;
  }

  try {
    const bitmap = await loadImageBitmap(file);
    const { width: w, height: h } = bitmap;

    // Ya está dentro del límite → no perder calidad recomprimiendo
    if (Math.max(w, h) <= maxDimension) {
      bitmap.close?.();
      return file;
    }

    const scale = maxDimension / Math.max(w, h);
    const targetW = Math.round(w * scale);
    const targetH = Math.round(h * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close?.();
      return file;
    }

    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close?.();

    // PNG con posible transparencia → mantener PNG; resto → JPEG (mejor compresión).
    const outputMime: string =
      opts.mimeType ?? (file.type === "image/png" ? "image/png" : "image/jpeg");

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outputMime, quality),
    );

    if (!blob) return file;

    // Solo reemplazar si efectivamente quedó más pequeño
    if (blob.size >= file.size) return file;

    const ext = outputMime === "image/png" ? "png" : "jpg";
    const baseName = file.name.replace(/\.[^.]+$/, "");
    const newName = `${baseName}.${ext}`;

    return new File([blob], newName, {
      type: outputMime,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

async function loadImageBitmap(file: File): Promise<ImageBitmap> {
  // createImageBitmap es la vía moderna y eficiente; soporta HEIC en Safari/iOS.
  if (typeof createImageBitmap === "function") {
    return await createImageBitmap(file);
  }
  // Fallback: HTMLImageElement
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    // ImageBitmap-like wrapper: solo lo necesario para drawImage
    return img as unknown as ImageBitmap;
  } finally {
    URL.revokeObjectURL(url);
  }
}
